// src/components/CleanDateBanner.jsx
// NSI-17 Block 2b — the Last Clean Date banner, shared by the General Journal,
// the Sanity Report, and Settings ▸ Period lock. All three READ the single
// journal_clean_state row and the Recompute button runs the same on-demand
// computation. The date deep-links to the first offending transaction's group
// (via the row_id path) when one exists.
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import HelpIcon from './HelpIcon';
import { formatDateTime, formatDate } from '../utils/format';
import { IcoLn } from '../shell/ActionIcons';

// Transient cache: the clean state is fetched once per session and reused across mounts,
// navigations, and collapse/re-expand — so re-opening the GL (or re-clicking the chip) doesn't refetch.
let cleanStateCache = null;

export default function CleanDateBanner({ onOpenGroup, onClose }) {
  const [state, setState] = useState(cleanStateCache);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (cleanStateCache) { setState(cleanStateCache); return; }   // already have it this session
    api.getJournalCleanState().then(s => { cleanStateCache = s; setState(s); }).catch(() => {});
  }, []);

  async function recompute() {
    setBusy(true); setErr('');
    try { const s = await api.recomputeJournalCleanState(); cleanStateCache = s; setState(s); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const stamp = state?.computed_at ? formatDateTime(state.computed_at) : null;
  const canLink = state?.first_bad_txn_id && onOpenGroup;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      background: state?.last_clean_date ? '#f0fdf4' : '#fffbeb',
      border: `1px solid ${state?.last_clean_date ? '#bbf7d0' : '#fde68a'}`,
      borderRadius: 8, padding: '8px 14px', marginBottom: 16, fontSize: 13,
      color: state?.last_clean_date ? '#166534' : '#92400e' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        {state === null ? 'Loading clean state…'
          : state.last_clean_date ? (
            <>Data clean through{' '}
              {canLink ? (
                <span onClick={() => onOpenGroup({ row_id: state.first_bad_txn_id })}
                  title="Open the first offending transaction's group"
                  style={{ fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                  {formatDate(state.last_clean_date)}
                </span>
              ) : <b>{formatDate(state.last_clean_date)}</b>}
              {stamp && <span style={{ color: '#64748b', fontWeight: 400 }}>&nbsp;(computed {stamp})</span>}
            </>
          ) : state.computed_at && state.detail && state.detail !== null && state.last_clean_date === null && state.detail.startsWith('No month-end')
            ? <>No clean month-end found{stamp && <span style={{ color: '#64748b' }}>&nbsp;(computed {stamp})</span>}</>
            : <>Clean date not computed yet — press Recompute.</>}
        <HelpIcon helpKey="concept.last_clean_date" />
      </span>
      {/* 297 — THE REASON IS NOW VISIBLE TEXT, not a title= tooltip. The walk has always STORED why it
          stopped (journal_clean_state.detail); the banner only exposed it on hover, so a user saw a date
          with no explanation. Nothing new is computed — this surfaces what was already there.
          NOTE the link still points at first_bad_txn, which is the SOURCE row, not the offending derived
          leg — pointing at the offending ROW is CARRIED to v0.38 (scope ruling), so the link is kept but
          now sits BESIDE a stated reason rather than standing in for one. */}
      {state?.detail && (
        <span style={{ fontSize: 11.5, color: state?.last_clean_date ? '#166534' : '#92400e', opacity: 0.9 }}>
          · {state.detail}
        </span>
      )}
      {state?.first_bad_txn_id && canLink && (
        <span onClick={() => onOpenGroup({ row_id: state.first_bad_txn_id })}
          style={{ fontSize: 11, color: '#1e40af', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
          title="Opens the SOURCE row this boundary failed on — not necessarily the row at fault">
          → first offending transaction ({state.first_bad_txn_table})
        </span>
      )}
      {err && <span style={{ color: '#dc2626', fontSize: 12 }}>{err}</span>}
      <button className="btn success sm" onClick={recompute} disabled={busy} style={{ marginLeft: 'auto' }}>
        {busy ? 'Computing…' : 'Recompute'}
      </button>
      {onClose && <button onClick={onClose} title="Hide" aria-label="Hide" style={{ marginLeft: 8, padding: '4px 8px', background: '#fff', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}><IcoLn name="close" /></button>}
    </div>
  );
}
